import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProxmoxService } from './proxmox.service';

export interface VirtualMachine {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'paused' | 'error';
  node: string;
  type: 'qemu' | 'lxc';
  cpu: number;
  memory: number; // in MB
  disk: number; // in GB
  ipAddress: string;
  clientId: string;
  createdAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class VmManagementService {
  private vms: VirtualMachine[] = [
    {
      id: 'vm-100',
      name: 'Web Server 1',
      status: 'running',
      node: 'node1',
      type: 'qemu',
      cpu: 2,
      memory: 4096,
      disk: 50,
      ipAddress: '192.168.1.100',
      clientId: '1',
      createdAt: new Date('2023-01-20')
    },
    {
      id: 'vm-101',
      name: 'Database Server',
      status: 'running',
      node: 'node1',
      type: 'qemu',
      cpu: 4,
      memory: 8192,
      disk: 100,
      ipAddress: '192.168.1.101',
      clientId: '1',
      createdAt: new Date('2023-01-25')
    },
    {
      id: 'vm-200',
      name: 'App Server 1',
      status: 'stopped',
      node: 'node2',
      type: 'qemu',
      cpu: 2,
      memory: 2048,
      disk: 30,
      ipAddress: '192.168.1.200',
      clientId: '2',
      createdAt: new Date('2023-04-01')
    },
    {
      id: 'vm-201',
      name: 'App Server 2',
      status: 'running',
      node: 'node2',
      type: 'qemu',
      cpu: 2,
      memory: 2048,
      disk: 30,
      ipAddress: '192.168.1.201',
      clientId: '2',
      createdAt: new Date('2023-04-01')
    },
    {
      id: 'vm-202',
      name: 'Load Balancer',
      status: 'running',
      node: 'node2',
      type: 'lxc',
      cpu: 1,
      memory: 1024,
      disk: 10,
      ipAddress: '192.168.1.202',
      clientId: '2',
      createdAt: new Date('2023-04-05')
    },
    {
      id: 'vm-300',
      name: 'Test Server',
      status: 'paused',
      node: 'node3',
      type: 'qemu',
      cpu: 1,
      memory: 1024,
      disk: 20,
      ipAddress: '192.168.1.300',
      clientId: '3',
      createdAt: new Date('2023-05-15')
    }
  ];

  private vmsSubject = new BehaviorSubject<VirtualMachine[]>(this.vms);
  public vms$ = this.vmsSubject.asObservable();

  constructor(private proxmoxService: ProxmoxService) { }

  getVMs(): Observable<VirtualMachine[]> {
    return of(this.vms);
  }

  getVMById(id: string): Observable<VirtualMachine | undefined> {
    const vm = this.vms.find(v => v.id === id);
    return of(vm);
  }

  getVMsByClientId(clientId: string): Observable<VirtualMachine[]> {
    const clientVMs = this.vms.filter(v => v.clientId === clientId);
    return of(clientVMs);
  }

  addVM(vm: Omit<VirtualMachine, 'id' | 'createdAt'>): Observable<VirtualMachine> {
    const newVM: VirtualMachine = {
      ...vm,
      id: `vm-${Date.now()}`,
      createdAt: new Date()
    };
    this.vms.push(newVM);
    this.vmsSubject.next(this.vms);
    return of(newVM);
  }

  updateVM(id: string, vm: Partial<VirtualMachine>): Observable<VirtualMachine | undefined> {
    const index = this.vms.findIndex(v => v.id === id);
    if (index !== -1) {
      this.vms[index] = { ...this.vms[index], ...vm };
      this.vmsSubject.next(this.vms);
      return of(this.vms[index]);
    }
    return of(undefined);
  }

  deleteVM(id: string): Observable<boolean> {
    const index = this.vms.findIndex(v => v.id === id);
    if (index !== -1) {
      this.vms.splice(index, 1);
      this.vmsSubject.next(this.vms);
      return of(true);
    }
    return of(false);
  }

  // VM Actions with real Proxmox API integration
  startVM(vmId: string): Observable<any> {
    const vm = this.vms.find(v => v.id === vmId);
    if (vm) {
      // Extract VM ID number (e.g., vm-100 -> 100)
      const vmNumber = parseInt(vm.id.split('-')[1]);
      
      // Call the Proxmox API to start the VM
      return this.proxmoxService.startVM(vm.node, vm.type, vmNumber).pipe(
        map(response => {
          console.log(`Started VM ${vmId} on node ${vm.node}`, response);
          vm.status = 'running';
          this.vmsSubject.next(this.vms);
          return { success: true, message: 'VM started successfully' };
        }),
        catchError(error => {
          console.error(`Error starting VM ${vmId}:`, error);
          return of({ success: false, message: `Error starting VM: ${error}` });
        })
      );
    }
    return of({ success: false, message: 'VM not found' });
  }

  stopVM(vmId: string): Observable<any> {
    const vm = this.vms.find(v => v.id === vmId);
    if (vm) {
      // Extract VM ID number (e.g., vm-100 -> 100)
      const vmNumber = parseInt(vm.id.split('-')[1]);
      
      // Call the Proxmox API to stop the VM
      return this.proxmoxService.stopVM(vm.node, vm.type, vmNumber).pipe(
        map(response => {
          console.log(`Stopped VM ${vmId} on node ${vm.node}`, response);
          vm.status = 'stopped';
          this.vmsSubject.next(this.vms);
          return { success: true, message: 'VM stopped successfully' };
        }),
        catchError(error => {
          console.error(`Error stopping VM ${vmId}:`, error);
          return of({ success: false, message: `Error stopping VM: ${error}` });
        })
      );
    }
    return of({ success: false, message: 'VM not found' });
  }

  restartVM(vmId: string): Observable<any> {
    const vm = this.vms.find(v => v.id === vmId);
    if (vm) {
      // Extract VM ID number (e.g., vm-100 -> 100)
      const vmNumber = parseInt(vm.id.split('-')[1]);
      
      // Call the Proxmox API to reset the VM
      return this.proxmoxService.resetVM(vm.node, vm.type, vmNumber).pipe(
        map(response => {
          console.log(`Restarted VM ${vmId} on node ${vm.node}`, response);
          vm.status = 'running';
          this.vmsSubject.next(this.vms);
          return { success: true, message: 'VM restarted successfully' };
        }),
        catchError(error => {
          console.error(`Error restarting VM ${vmId}:`, error);
          return of({ success: false, message: `Error restarting VM: ${error}` });
        })
      );
    }
    return of({ success: false, message: 'VM not found' });
  }

  pauseVM(vmId: string): Observable<any> {
    const vm = this.vms.find(v => v.id === vmId);
    if (vm) {
      // Extract VM ID number (e.g., vm-100 -> 100)
      const vmNumber = parseInt(vm.id.split('-')[1]);
      
      // Call the Proxmox API to suspend the VM
      return this.proxmoxService.suspendVM(vm.node, vm.type, vmNumber).pipe(
        map(response => {
          console.log(`Paused VM ${vmId} on node ${vm.node}`, response);
          vm.status = 'paused';
          this.vmsSubject.next(this.vms);
          return { success: true, message: 'VM paused successfully' };
        }),
        catchError(error => {
          console.error(`Error pausing VM ${vmId}:`, error);
          return of({ success: false, message: `Error pausing VM: ${error}` });
        })
      );
    }
    return of({ success: false, message: 'VM not found' });
  }

  resumeVM(vmId: string): Observable<any> {
    const vm = this.vms.find(v => v.id === vmId);
    if (vm) {
      // Extract VM ID number (e.g., vm-100 -> 100)
      const vmNumber = parseInt(vm.id.split('-')[1]);
      
      // Call the Proxmox API to resume the VM
      return this.proxmoxService.resumeVM(vm.node, vm.type, vmNumber).pipe(
        map(response => {
          console.log(`Resumed VM ${vmId} on node ${vm.node}`, response);
          vm.status = 'running';
          this.vmsSubject.next(this.vms);
          return { success: true, message: 'VM resumed successfully' };
        }),
        catchError(error => {
          console.error(`Error resuming VM ${vmId}:`, error);
          return of({ success: false, message: `Error resuming VM: ${error}` });
        })
      );
    }
    return of({ success: false, message: 'VM not found' });
  }
}